import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import './AuthenticationDialog.css';

const AuthenticationDialog = ({ 
    open, 
    onClose, 
    onAuthenticate, 
    employees = [], 
    title = "Authentication Required",
    message = "Please select an employee and enter their password to continue."
}) => {
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            // Reset form when dialog opens
            setSelectedEmployee(null);
            setPassword('');
            setError('');
            setShowPassword(false);
        }
    }, [open]);

    const handleSubmit = () => {
        if (!selectedEmployee) {
            setError('Please select an employee');
            return;
        }
        if (!password) {
            setError('Please enter password');
            return;
        }

        // Call the authentication callback
        onAuthenticate(selectedEmployee, password);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            className="auth-dialog"
        >
            <DialogTitle className="auth-dialog-title">
                <i className="bi bi-shield-lock" style={{ marginRight: '8px' }}></i>
                {title}
            </DialogTitle>
            <DialogContent className="auth-dialog-content">
                <p className="auth-dialog-message">{message}</p>
                
                <div className="auth-form-field">
                    <Autocomplete
                        options={employees || []}
                        getOptionLabel={(option) => option?.staff_name || ''}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        value={selectedEmployee}
                        onChange={(e, newValue) => {
                            setSelectedEmployee(newValue);
                            setError('');
                        }}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Select Employee *" 
                                focused
                                error={!!error && !selectedEmployee}
                            />
                        )}
                        fullWidth
                    />
                </div>

                <div className="auth-form-field">
                    <TextField
                        label="Password *"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError('');
                        }}
                        onKeyPress={handleKeyPress}
                        focused
                        fullWidth
                        error={!!error && !password}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    size="small"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            )
                        }}
                    />
                </div>

                {error && (
                    <p className="auth-error-message">{error}</p>
                )}
            </DialogContent>
            <DialogActions className="auth-dialog-actions">
                <Button onClick={onClose} variant="outlined" color="error">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Authenticate
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AuthenticationDialog;
